import { useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';

import { IconButton } from './IconButton.js';
import { Icon } from './Icon.js';
import { useBodyScrollLock, useFocusTrap, useOnEscape } from './overlay.js';

export type DrawerSide = 'right' | 'left' | 'bottom';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  side?: DrawerSide;
  title?: ReactNode;
  children: ReactNode;
  ariaLabel?: string;
}

/**
 * A slide-in panel anchored to a screen edge. Handy for the notifications list,
 * mobile menus, filters, and the builder's code drawer.
 */
export function Drawer({ open, onClose, side = 'right', title, children, ariaLabel }: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useBodyScrollLock(open);
  useOnEscape(open, onClose);
  useFocusTrap(open, panelRef);

  if (!open) return null;

  return createPortal(
    <div
      className="pt-overlay pt-drawer-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className={`pt-drawer pt-drawer--${side}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : ariaLabel}
        tabIndex={-1}
      >
        <div className="pt-drawer__header">
          {title ? (
            <h2 className="pt-drawer__title" id={titleId}>
              {title}
            </h2>
          ) : (
            <span />
          )}
          <IconButton label="Close" icon={<Icon name="close" size={20} />} onClick={onClose} />
        </div>
        <div className="pt-drawer__body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
